<template>
    <v-card>
        <v-card-title primary-title>Cloning/Synthesis Failure Reporting</v-card-title>
        <v-card-text>
            <v-row>
                <p class="mb-4">
                    Use this form to report failures encountered during cloning or synthesis.
                    Clearing the failure reason will reset the production result.
                </p>
            </v-row>
            <v-row>
                <v-col cols="6">
                    <v-text-field
                        v-model="peptideCodeFilter"
                        label="Filter by Peptide Code"
                        class="mb-4"
                        variant="outlined"
                    ></v-text-field>
                </v-col>
                <v-col cols="6" class="text-right">
                    <v-checkbox
                        v-model="showOnlyFailedItems"
                        label="Show only failed items"
                        class="mb-4"
                    ></v-checkbox>
                </v-col>
            </v-row>
            <v-row>
                <v-data-table
                    :headers="headers"
                    :items="filteredOrderItems"
                    class="elevation-1"
                    :search="peptideCodeFilter"
                >
                    <template v-slot:item.peptideCode="{ item }">
                        {{ item.peptideCode }}
                    </template>
                    <template v-slot:item.productionResult="{ item }">
                        <v-chip
                            :color="item.productionResult === 'Fail' ? 'red' : 'gray'"
                            class="ma-2"
                        >
                            {{ item.productionResult || "None" }}
                        </v-chip>
                    </template>
                    <template v-slot:item.failureReason="{ item }">
                        <v-autocomplete
                            v-model="item.failureReason"
                            :items="[
                                MicrobialFailureReason.DNA_SYNTHESIS_FAILURE,
                                MicrobialFailureReason.CLONING_FAILURE,
                            ]"
                            item-title="label"
                            item-value="value"
                            clearable
                        />
                    </template>
                </v-data-table>
            </v-row>
        </v-card-text>
        <v-card-actions>
            <v-btn color="primary" @click="close">Cancel</v-btn>
            <v-btn color="primary" @click="submit">Submit</v-btn>
        </v-card-actions>
    </v-card>
</template>

<script lang="ts" setup>
import { ref, onMounted, watch, computed } from "vue";
import {
    MicrobialFailureReason,
    MicrobialPeptideBatchOrderItem,
    ordersAPI,
    SavedOrder,
} from "@/services/orders/ordersAPI";
import { VDataTableHeader } from "@/vuetifyTypeFixes";
import { withLoadingScreen } from "@/lib/loading";
import _ from "lodash";

const props = defineProps<{
    order: SavedOrder;
}>();

const originalOrderItems = ref<MicrobialPeptideBatchOrderItem[]>([]);
const editedOrderItems = ref<MicrobialPeptideBatchOrderItem[]>([]);
const peptideCodeFilter = ref<string>("");
const showOnlyFailedItems = ref<boolean>(false);
const headers = ref<VDataTableHeader[]>([
    { title: "Peptide Code", value: "peptideCode", width: "25%" },
    { title: "Production Result", value: "productionResult", width: "25%" },
    { title: "Failure Reason", value: "failureReason", width: "50%" },
]);

const emit = defineEmits<{
    (e: "closeForm"): void;
}>();

onMounted(async () => {
    withLoadingScreen(async () => {
        originalOrderItems.value = await ordersAPI.fetchOrderItems(props.order.id);
        editedOrderItems.value = _.cloneDeep(originalOrderItems.value);
    });
});

const filteredOrderItems = computed(() => {
    // filter out non-failed items if the checkbox is checked
    return editedOrderItems.value.filter((item) => {
        if (peptideCodeFilter.value && !item.peptideCode.includes(peptideCodeFilter.value)) {
            return false;
        }
        if (showOnlyFailedItems.value && item.productionResult !== "Fail") {
            return false;
        }
        return true;
    });
});

// if an order item failure reason is set, update the same item's production result
watch(
    editedOrderItems,
    (newItems) => {
        newItems.forEach((item) => {
            if (item.failureReason) {
                item.productionResult = "Fail";
            } else {
                item.productionResult = null;
            }
        });
    },
    { deep: true }
);

function submit() {
    // determine which items have updated failure reasons
    const updatedItems = editedOrderItems.value.filter((item, index) => {
        return (
            item.failureReason !== originalOrderItems.value[index].failureReason
        );
    });
    if (updatedItems.length === 0) {
        console.log("No items were updated.");
        emit("closeForm");
        return;
    }
    withLoadingScreen(async () => {
        await ordersAPI.updateOrderItems(props.order.id, updatedItems);
    });
    emit("closeForm");
}

function close() {
    emit("closeForm");
}
</script>
